import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { Row, Col, Button } from 'reactstrap';
import moment from 'moment';
import Sidebar from '../../Sidebar';
import { useNavigate, useParams } from 'react-router';
import { useCookies } from 'react-cookie';
import FloatingControls from '../../components/FloatingControls';
import FormPageLayout from '../../components/FormPageLayout';
import LoadingOverlay from '../../components/LoadingOverlay';
import ErrorToast from '../../ErrorToast';
import { displayError } from '../../helpers';
import { getUserAllowedPlants } from '../../services/PlantServices';
import { getAllEquipments } from '../../services/EquipmentServices';
import {
    getMaintenanceAutofill,
    getMaintenanceDailyDetails,
    updateMaintenanceDaily,
} from '../../services/MaintenanceServices';
import MaintenanceAssetSections from './MaintenanceAssetSections';
import {
    buildDefaultDetailList,
    flattenGroupedLines,
    groupLinesByCategory,
    mergeAutofillSuggestions,
} from './maintenanceConstants';

function DailyPlantLogEdit() {
    const { id } = useParams();
    const [cookies] = useCookies(['myToken']);
    const navigate = useNavigate();
    const [plants, setPlants] = useState([]);
    const [equipments, setEquipments] = useState([]);
    const [inProgress, setInProgress] = useState(false);
    const [isActive, setIsActive] = useState(true);
    const [inputs, setInputs] = useState({
        plant_id: '',
        log_date: '',
        prepared_by: '',
        user_remarks: '',
    });
    const [groupedLines, setGroupedLines] = useState(() => groupLinesByCategory(buildDefaultDetailList()));

    useEffect(() => {
        getUserAllowedPlants(cookies).then((response) => {
            setPlants((response.plant_list || [])
                .filter((p) => p.status)
                .map((p) => ({ value: p.id, label: p.plant_alias })));
        });
        getAllEquipments(cookies).then((response) => {
            const list = response.equipment_list || [];
            setEquipments(list.filter((e) => e.status === true || e.status === 1 || e.status === 'Active'));
        });
    }, [cookies]);

    useEffect(() => {
        if (!id) return;
        setInProgress(true);
        getMaintenanceDailyDetails(cookies, id)
            .then((data) => {
                setIsActive(data.status === true || data.status === 1 || data.status === 'Active');
                setInputs({
                    plant_id: data.plant_id,
                    log_date: moment(data.log_date, 'DD-MM-YYYY').format('YYYY-MM-DD'),
                    prepared_by: data.prepared_by || '',
                    user_remarks: data.user_remarks || '',
                });
                const detailList = data.detail_list?.length ? data.detail_list : buildDefaultDetailList();
                setGroupedLines(groupLinesByCategory(detailList));
            })
            .catch((error) => displayError(error?.response?.data || error, 'Load Failed'))
            .finally(() => setInProgress(false));
    }, [cookies, id]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setInputs((prev) => ({ ...prev, [name]: value }));
    };

    const handleAutofill = () => {
        if (!inputs.plant_id || !inputs.log_date) return;
        setInProgress(true);
        const displayDate = moment(inputs.log_date, 'YYYY-MM-DD').format('DD-MM-YYYY');
        getMaintenanceAutofill(cookies, inputs.plant_id, displayDate)
            .then((response) => {
                const currentFlat = flattenGroupedLines(groupedLines);
                const merged = mergeAutofillSuggestions(currentFlat, response.suggestions || []);
                setGroupedLines(groupLinesByCategory(merged));
            })
            .catch((error) => displayError(error?.response?.data || error, 'Autofill Failed'))
            .finally(() => setInProgress(false));
    };

    const payload = useMemo(() => ({
        plant_id: inputs.plant_id,
        log_date: moment(inputs.log_date, 'YYYY-MM-DD').format('DD-MM-YYYY'),
        prepared_by: inputs.prepared_by,
        user_remarks: inputs.user_remarks,
        detail_list: flattenGroupedLines(groupedLines),
    }), [groupedLines, inputs]);

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!isActive) {
            Swal.fire('Cancelled log cannot be edited.');
            return;
        }
        setInProgress(true);
        updateMaintenanceDaily(cookies, id, payload)
            .then(() => Swal.fire({ icon: 'success', title: 'Daily plant log updated' }))
            .catch((error) => displayError(error?.response?.data || error, 'Update Failed'))
            .finally(() => setInProgress(false));
    };

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <ErrorToast />
            <LoadingOverlay active={inProgress} />
            <div id="page-wrap">
                <form onSubmit={handleSubmit}>
                    <FloatingControls
                        tableLink="/DailyPlantLogTable"
                        formLink="/DailyPlantLog"
                        addNewLabel="Main Form"
                        enableCancel
                        onCancel={() => navigate('/DailyPlantLogTable')}
                    />
                    <FormPageLayout
                        moduleLabel="MAINTENANCE"
                        title="Daily Plant Log"
                        subtitle="Update day-wise asset performance using tabs — breakdown and idle hours per asset."
                        modeLabel={isActive ? 'UPDATE MODE' : 'CANCELLED'}
                    >
                        <div className="container form-control-panel">
                            <Row>
                                <Col xl={6} lg={12}>
                                    <div className="form-row table-bordered shadow p-2 my-2 border-secondary mb-3">
                                        <label className="form-group col-sm-4 text-right">Plant</label>
                                        <select
                                            name="plant_id"
                                            className="form-control col-sm-7"
                                            value={inputs.plant_id}
                                            disabled
                                        >
                                            <option value="">Select Plant</option>
                                            {plants.map((p) => (
                                                <option key={p.value} value={p.value}>{p.label}</option>
                                            ))}
                                        </select>

                                        <label className="form-group col-sm-4 text-right">Date</label>
                                        <input
                                            type="date"
                                            name="log_date"
                                            className="form-control col-sm-7"
                                            value={inputs.log_date}
                                            onChange={handleChange}
                                            disabled={!isActive}
                                        />

                                        <label className="form-group col-sm-4 text-right">Prepared By</label>
                                        <input
                                            name="prepared_by"
                                            className="form-control col-sm-7"
                                            value={inputs.prepared_by}
                                            onChange={handleChange}
                                            readOnly={!isActive}
                                        />

                                        <label className="form-group col-sm-4 text-right">Remarks</label>
                                        <input
                                            name="user_remarks"
                                            className="form-control col-sm-7"
                                            value={inputs.user_remarks}
                                            onChange={handleChange}
                                            readOnly={!isActive}
                                        />

                                        {isActive && (
                                            <div className="col-sm-12 text-right mt-2">
                                                <Button type="button" color="info" size="sm" onClick={handleAutofill}>
                                                    Autofill from DC / Job / Gate
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </Col>
                            </Row>

                            <MaintenanceAssetSections
                                groupedLines={groupedLines}
                                onGroupedChange={setGroupedLines}
                                equipments={equipments}
                                readOnly={!isActive}
                                plantId={inputs.plant_id}
                                logDate={inputs.log_date}
                                cookies={cookies}
                            />

                            {isActive && (
                                <div className="text-right mb-4">
                                    <Button color="primary" type="submit">Update Daily Log</Button>
                                </div>
                            )}
                        </div>
                    </FormPageLayout>
                </form>
            </div>
        </div>
    );
}

export default DailyPlantLogEdit;
